/**
 * The parts of the block that are worth testing, kept away from the parts that draw.
 *
 * Nothing in this file does a sum on money. It reads what the server sent, decides which
 * button is pressed, and builds the request that asks for a payment page. The amount in
 * pence is never worked out here and never sent from here — only a key. See the worker's
 * `amounts.ts` for why.
 */

/** One amount button, as the worker described it. */
export interface Amount {
  /** What is sent when this button is pressed. Never pounds. */
  readonly key: string;
  /** What the button reads: `£10`. Formatted by the worker, so every page agrees. */
  readonly label: string;
  /** The second line. Copy, not money. */
  readonly descriptor: string;
}

/**
 * Read the amount list the page printed onto the element.
 *
 * The list comes from the worker, through the WordPress shortcode, so the words on the page
 * and the pence in the worker cannot drift apart. See `../../wordpress/mu-plugins/`.
 *
 * Returns null rather than a half-built list. A block that cannot state its amounts must not
 * show amount buttons, and the element falls back to showing only the business route.
 */
export function readAmounts(raw: unknown): readonly Amount[] | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  const amounts: Amount[] = [];
  for (const item of parsed) {
    if (item === null || typeof item !== 'object') return null;
    const row = item as Record<string, unknown>;
    if (typeof row['key'] !== 'string' || row['key'] === '') return null;
    if (typeof row['label'] !== 'string' || row['label'] === '') return null;
    amounts.push({
      key: row['key'],
      label: row['label'],
      descriptor: typeof row['descriptor'] === 'string' ? row['descriptor'] : '',
    });
  }

  // Two buttons with the same key would make the tick appear twice and the selection
  // ambiguous. Refuse the whole list rather than draw something misleading.
  const keys = new Set(amounts.map((a) => a.key));
  if (keys.size !== amounts.length) return null;

  return amounts;
}

/** What the block sends when it asks for a payment page. */
export interface DonateRequest {
  readonly project: string;
  readonly amount: string;
  readonly customAmount: string;
  readonly sourcePage: string;
  /**
   * Whether the reader left the newsletter tick box ticked.
   *
   * Sent as a real true or false, never as a string, because the worker only counts a real
   * `true` as permission. See `handleDonate` there.
   */
  readonly newsletter: boolean;
}

/**
 * Build that request.
 *
 * `customAmount` always travels, and the worker reads it only when `amount` is the free-box
 * key. Sending it either way keeps this function free of a decision the worker already makes,
 * so the two cannot disagree about when the free box counts.
 */
export function buildDonateRequest(
  project: string,
  amountKey: string,
  customText: string,
  sourcePage: string,
  newsletter: boolean,
): DonateRequest {
  return {
    project,
    amount: amountKey,
    customAmount: customText,
    // Trimmed here so a very long address never becomes the reason a payment fails. The
    // worker trims it again; it does not rely on this.
    sourcePage: sourcePage.slice(0, 200),
    newsletter,
  };
}

export type DonateAnswer =
  | { readonly ok: true; readonly payUrl: string }
  | { readonly ok: false; readonly message: string };

/** What the block says when it does not know what went wrong. Never a status code. */
export const GENERIC_FAILURE =
  'We couldn’t open the payment window. Please try again. Nothing has been charged.';

/**
 * Is this an address we are willing to send somebody's browser to?
 *
 * The block navigates the visitor away using whatever comes back. That is the one moment
 * this code hands control to a string it did not write, so the string is checked. Only
 * Stripe's own hosted checkout, and only over HTTPS.
 *
 * If C Level ever puts Stripe checkout on its own domain, this list is the one line that
 * changes — and it should change on purpose, not by a response saying so.
 */
export function isStripeCheckoutUrl(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  return parsed.hostname === 'checkout.stripe.com';
}

/**
 * Read the worker's answer.
 *
 * The worker's own refusals are written for a visitor — "That is not one of the amounts we
 * offer." — so they are shown as they arrive. Anything unrecognisable becomes the generic
 * sentence, because a raw response is not something to put in front of a reader.
 */
export function readDonateAnswer(body: unknown): DonateAnswer {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return { ok: false, message: GENERIC_FAILURE };
  }
  const row = body as Record<string, unknown>;

  if (typeof row['error'] === 'string' && row['error'] !== '') {
    return { ok: false, message: row['error'] };
  }
  if (isStripeCheckoutUrl(row['payUrl'])) {
    return { ok: true, payUrl: row['payUrl'] as string };
  }
  return { ok: false, message: GENERIC_FAILURE };
}

/**
 * Did the visitor just come back from a completed payment?
 *
 * THIS IS NOT PROOF OF PAYMENT and the block's wording must never treat it as such. It is a
 * query string, and anybody can type one. The block says thank you because it is polite to
 * somebody who did pay; the money is only recorded when Stripe's own message reaches the
 * worker's webhook.
 */
export function cameBackFromPayment(search: string): boolean {
  return new URLSearchParams(search).get('funded') === 'yes';
}

/**
 * The amount that came back on the return address, as a label, or an empty string.
 *
 * Only used to say "your £10" in the thank-you. It is read from the address bar, so it is
 * checked to be a plain small number of pence and nothing else — otherwise the address bar
 * becomes a way to put chosen text on the page.
 */
export function amountLabelFromReturn(search: string): string {
  const pence = wholeNumberFrom(search, 'amount');
  return pence === null ? '' : formatPenceLocally(pence);
}

/**
 * The credits that came back on the return address, as words, or an empty string.
 *
 * Same care as the amount, for the same reason: the address bar is not a trusted source, and
 * this text is printed on the page. A reader who reloads the thank-you with `kg=99999999` in
 * the address must not see a claim we never made.
 */
export function creditsLabelFromReturn(search: string): string {
  const kilograms = wholeNumberFrom(search, 'kg');
  if (kilograms === null) return '';
  // A hard ceiling on what this will ever print. The block cannot sell more than £1,000 of
  // credits in one payment, which is about 31,700 kg, so anything past that came from the
  // address bar rather than from us.
  if (kilograms > 40_000) return '';
  if (kilograms < 1_000) return `${kilograms} kg`;
  const hundredths = Math.round(kilograms / 10);
  return `${Math.floor(hundredths / 100)}.${String(hundredths % 100).padStart(2, '0')} tonnes`;
}

/** Read one plain whole number above zero from the address bar, or nothing at all. */
function wholeNumberFrom(search: string, name: string): number | null {
  const raw = new URLSearchParams(search).get(name);
  if (raw === null || !/^\d{1,8}$/.test(raw)) return null;
  const value = Number.parseInt(raw, 10);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

/**
 * Pence as words, for the thank-you line only.
 *
 * A deliberate second copy of the worker's `formatPence`, and it is allowed to exist for one
 * reason: it never decides what anybody is charged. It only reads back a figure that has
 * already been taken. The test file pins the two to the same answers.
 */
export function formatPenceLocally(pence: number): string {
  const pounds = Math.floor(pence / 100);
  const remainder = pence % 100;
  const withCommas = String(pounds).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return remainder === 0 ? `£${withCommas}` : `£${withCommas}.${String(remainder).padStart(2, '0')}`;
}

/** The path of the page the block sits on, for the record on the payment. */
export function sourcePageOf(pathname: string): string {
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}
